import i18n from '@/utils/i18n';
import {
  buildBookmarkedSection,
  buildEditsSection,
  buildPeerAverageSection,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-special-sections';
import {
  buildRangeSections,
  buildUOMSection,
  buildWeeklyPurchaseQuantitiesSection,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-column-sections';
import { CategoryOption } from '@sales-portal/domain/pricing/screens/management/blocs/utils/category-filter';
import { ColumnId } from '@sales-portal/domain/pricing/screens/management/types';
import {
  PeerRange,
  VariantPackCostChange,
} from '@sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import {
  PillFilterSection,
  PricingFilterSection,
  PricingFilterSectionKind,
  PricingFilterSpecialSectionId,
  PricingRangeColumnConfig,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';

interface EditsFilterConfig<T> {
  counts: Record<string, number>;
  getRowId: (row: T) => string;
}

interface BookmarkedFilterConfig<T> {
  bookmarkedByRow: Record<string, boolean>;
  getRowId: (row: T) => string;
}

interface PeerAverageFilterConfig<T> {
  getCurrentGrossMargin: (row: T) => number;
  getPeerRange: (row: T) => PeerRange | null;
}

interface BuildPricingFilterSectionsArgs<T extends VariantPackCostChange> {
  bookmarked?: BookmarkedFilterConfig<T>;
  categoryOptions: CategoryOption[];
  columnIds: string[];
  editCounts?: EditsFilterConfig<T>;
  peerAverage?: PeerAverageFilterConfig<T>;
  rangeColumnConfigs: Record<string, PricingRangeColumnConfig<T>>;
  rows: T[];
  timezone: string;
}

const buildCategorySection = <T extends VariantPackCostChange>(
  categoryOptions: CategoryOption[],
): PillFilterSection<T> => ({
  getValues: (row) => row.groups.map((g) => g.uuid),
  id: PricingFilterSpecialSectionId.Category,
  kind: PricingFilterSectionKind.Pill,
  options: categoryOptions.map((option) => ({
    label: option.displayName,
    value: option.uuid,
  })),
  singleSelect: false,
  title: i18n.t('pricing.filters.category.title'),
});

export const buildPricingFilterSections = <T extends VariantPackCostChange>({
  bookmarked,
  categoryOptions,
  columnIds,
  editCounts,
  peerAverage,
  rangeColumnConfigs,
  rows,
  timezone,
}: BuildPricingFilterSectionsArgs<T>): PricingFilterSection<T>[] => {
  const sections: PricingFilterSection<T>[] = [];

  if (editCounts) {
    sections.push(buildEditsSection(editCounts.counts, editCounts.getRowId));
  }
  if (bookmarked) {
    sections.push(
      buildBookmarkedSection(bookmarked.bookmarkedByRow, bookmarked.getRowId),
    );
  }
  if (peerAverage) {
    sections.push(buildPeerAverageSection(peerAverage));
  }

  for (const columnId of columnIds) {
    if (columnId === ColumnId.UOM) {
      // Filters survive hiding the column. Category is absorbed after UOM.
      sections.push(buildUOMSection(ColumnId.UOM, rows));
      sections.push(buildCategorySection(categoryOptions));
      continue;
    }

    if (columnId === ColumnId.WeeklyPurchaseQuantities) {
      sections.push(
        buildWeeklyPurchaseQuantitiesSection(columnId, rows, timezone),
      );
      continue;
    }

    const config = rangeColumnConfigs[columnId];
    if (!config) {
      continue;
    }

    sections.push(...buildRangeSections(columnId, config, rows));
  }

  return sections;
};
